import { Controller } from "@hotwired/stimulus"
import { toBlob } from "html-to-image"

// Rasterise an AIRDROP slide to a PNG and write it to the clipboard, so a slide
// drops straight into a deck or email.
//
// Models the image-copy pattern from image_context_menu_controller.js
// (navigator.clipboard.write([new ClipboardItem(...)])) and reuses html-to-image
// — already a WIN dependency, used by snip_controller and chart_preview_controller
// — rather than adding html2canvas.
//
// The canvas target is the un-scaled slide node; capturing it at its natural
// size gives a full-resolution raster regardless of the on-screen scale. The
// capture dimensions are the deck's own canvas (ADR-0077), supplied as the
// width / height Stimulus values, defaulting to the slidepack canvas (1280x720).
//
// Server fallback (card #2233): a slide containing an un-overridden "html"-kind
// element renders that element inside a sandboxed <iframe srcdoc
// sandbox="allow-scripts"> (ADR-0095), deliberately WITHOUT allow-same-origin —
// a real security boundary, not a bug. html-to-image runs in the PARENT
// document, so it has no read access to that iframe's contentDocument at all —
// it can never rasterise that region client-side, no matter what we try here.
// `sandbox="allow-scripts"` (with no allow-same-origin) is unique to that ONE
// case in _canvas.html.erb — the "analytics" iframe kind carries no sandbox
// attribute at all — so checking for it up front is a cheap, unambiguous way
// to detect "this slide is doomed client-side" BEFORE wasting a client-side
// attempt, and to route straight to DecksController#slide_render_proxy — a
// server-side Playwright render of just that one slide
// (Airdrop::DeckSlideCapture) — instead. Every other slide is completely
// unaffected: the existing client-side capture below is untouched.
export default class extends Controller {
  static targets = ["canvas", "button", "label"]
  static values = {
    width: { type: Number, default: 1280 },
    height: { type: Number, default: 720 },
    deckId: Number,
    slideId: String
  }

  async copy() {
    const node = this.canvasTarget
    const button = this.hasButtonTarget ? this.buttonTarget : null

    // Jay: "make a spinner icon along with the generating text ... so its
    // more obvious stuff is happening" -- disable immediately, before even
    // the iframe-sandbox check, so the button's group-disabled spinner
    // (_slide_toolbar.html.erb) shows for the FULL round trip on both the
    // client-side path below and the copyViaServer() fallback, not just the
    // fallback's genuinely-slower server render. Reset happens in the
    // top-level finally so every exit path (iframe fallback excepted --
    // copyViaServer manages its own disable/enable) re-enables the button.
    if (button) button.disabled = true

    if (this.hasSandboxedHtmlIframe(node)) {
      return this.copyViaServer(button)
    }

    try {
      await this.copyClientSide(node, button)
    } finally {
      if (button) button.disabled = false
    }
  }

  // Card #2182: `_canvas.html.erb` (card #2336) now renders this iframe's
  // sandbox attribute with two extra tokens --
  // `allow-scripts allow-popups allow-top-navigation-by-user-activation` --
  // so `iframe[sandbox="allow-scripts"]` (an EXACT full-string match) no
  // longer matches any real html-kind slide's iframe, silently sending every
  // one of them down the client-side path that can never rasterise sandboxed
  // content (see the class comment above). Match on the `allow-scripts`
  // TOKEN being present in the space-separated attribute value instead, so
  // this keeps working regardless of what other tokens get added in future.
  hasSandboxedHtmlIframe(node) {
    return Array.from(node.querySelectorAll("iframe")).some((iframe) =>
      (iframe.getAttribute("sandbox") || "").split(/\s+/).includes("allow-scripts")
    )
  }

  async copyClientSide(node, button) {
    // Card #2325: wait for webfonts (+ any unsandboxed analytics iframe) to
    // actually be ready BEFORE touching styles or rasterising below. Without
    // this, toBlob() could capture mid font-swap and silently drop every
    // text glyph while shapes/images (no font dependency) rendered fine —
    // the exact failure mode waitUntilPrintReady() (card #2167) already
    // fixed on the sibling PDF/print path. Awaited here, ahead of the
    // margin/transform tweaks, so the slide isn't left in its temporarily
    // adjusted state for the duration of the wait.
    await this.waitUntilCaptureReady(node)

    // The canvas target carries `mt-4` (card #2079, spacing it below the
    // slide toolbar added in the same wrapper) — a margin, not part of the
    // node's own box. html-to-image's clone-and-rasterise still bakes it in,
    // and since we request an explicit 1280x720 output, the whole clone
    // (720 + 16px margin tall) gets squeezed to fit — leaving a blank strip
    // the margin's height across the top of the raster and clipping the
    // slide's own rounded top corners. Zero it for the capture only; restore
    // immediately after so the on-screen layout is untouched.
    const originalMarginTop = node.style.marginTop
    node.style.marginTop = "0px"

    // `node` (.airdrop-slide-scale) is the RESPONSIVE wrapper — on screen it's
    // usually narrower than the deck's own 1280x720 canvas (its `width: 100%`
    // only reaches full canvas size on a wide viewport), and its `.airdrop-slide`
    // child is scaled down to fit via a `scale()` transform (see
    // application.css). html-to-image's cloneNode bakes in each element's
    // CURRENT getComputedStyle onto the clone (including that resolved scale
    // transform), then separately forces the clone's own width/height to the
    // requested output size (1280x720 here) — but it only does that for the
    // root node, not this transformed child. The result: the outer wrapper
    // clone is correctly forced to 1280x720, but the inner slide keeps
    // whatever smaller on-screen scale it had, rendering it anchored top-left
    // at native-times-that-scale size and leaving the raster's right/bottom
    // blank. Forcing the transform to identity for the capture only makes the
    // inner slide fill the (now correctly-sized) 1280x720 wrapper exactly.
    const innerSlide = node.querySelector(".airdrop-slide")
    const originalTransform = innerSlide ? innerSlide.style.transform : null
    if (innerSlide) innerSlide.style.setProperty("transform", "none", "important")

    try {
      const blob = await toBlob(node, {
        width: this.widthValue > 0 ? this.widthValue : 1280,
        height: this.heightValue > 0 ? this.heightValue : 720,
        pixelRatio: 2,
        cacheBust: true
      })
      if (!blob) throw new Error("toBlob returned null")

      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
      this.flash(button, "Copied!", "btn-secondary")
    } catch (error) {
      this.flash(button, "Copy failed", "btn-delete")
    } finally {
      node.style.marginTop = originalMarginTop
      if (innerSlide) {
        if (originalTransform) {
          innerSlide.style.setProperty("transform", originalTransform)
        } else {
          innerSlide.style.removeProperty("transform")
        }
      }
    }
  }

  // Best-effort wait for webfonts + embedded analytics iframes before
  // rasterising (card #2325). Mirrors airdrop_report_viewer_controller.js's
  // waitUntilPrintReady() (card #2167), built for the identical race on the
  // sibling PDF/print path — see that method's comment for the full
  // rationale. Scoped to `node` (the capture target) rather than the whole
  // controller element, since that's this controller's equivalent unit.
  //
  // - document.fonts.ready resolves once every requested font has finished
  //   loading and the browser has done a layout pass with it.
  // - Any "analytics" iframe (unsandboxed — a slide with a sandboxed
  //   `allow-scripts` iframe never reaches here, see copyViaServer() above)
  //   gets its own `load` listener, capped by IFRAME_TIMEOUT_MS so one slow
  //   or broken embed can never hang Copy Slide forever.
  // - A short grace delay AFTER the iframe loads resolve, only when iframes
  //   are present: an iframe's `load` event covers its document load, not
  //   its own post-load AJAX chart paint, so this narrows (does not fully
  //   eliminate) the analytics race.
  async waitUntilCaptureReady(node) {
    const waits = []
    if (document.fonts?.ready) waits.push(document.fonts.ready)

    const iframes = Array.from(node.querySelectorAll("iframe"))
    const IFRAME_TIMEOUT_MS = 4000
    iframes.forEach((iframe) => {
      waits.push(new Promise((resolve) => {
        if (iframe.contentDocument?.readyState === "complete") return resolve()
        const done = () => resolve()
        iframe.addEventListener("load", done, { once: true })
        setTimeout(done, IFRAME_TIMEOUT_MS)
      }))
    })

    await Promise.all(waits)
    if (iframes.length) await new Promise((resolve) => setTimeout(resolve, 800))
  }

  // Server-render fallback (card #2233). Fetches a PNG of this ONE slide from
  // DecksController#slide_render_proxy and writes it to the clipboard the
  // exact same way the client-side path above does. Takes noticeably longer
  // (a real headless render, ~1-3s typically) than the instant client-side
  // capture, but shows the SAME "Generating…" spinner state as the client
  // path now (see copy() above) -- disabling the button is enough to reveal
  // it via the group-disabled CSS variant, so there's no separate
  // "Rendering…" string to manage here any more.
  async copyViaServer(button) {
    if (button) button.disabled = true

    try {
      if (!this.hasDeckIdValue || !this.hasSlideIdValue) {
        throw new Error("copy-slide is missing deck/slide id values for the server render")
      }

      const url = `/decks/${this.deckIdValue}/slide_render_proxy?slide_id=${encodeURIComponent(this.slideIdValue)}`
      const response = await fetch(url, { headers: { Accept: "image/png" } })
      if (!response.ok) throw new Error(`slide render proxy returned HTTP ${response.status}`)

      const blob = await response.blob()
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])

      this.flash(button, "Copied!", "btn-secondary")
    } catch (error) {
      this.flash(button, "Copy failed", "btn-delete")
    } finally {
      if (button) button.disabled = false
    }
  }

  // Sets the flash message on the LABEL span, not the whole button
  // (_slide_toolbar.html.erb now gives the button two child spans -- a
  // default label and a spinner+"Generating…" pair -- rather than bare
  // text, so overwriting button.textContent directly would permanently
  // destroy that structure the first time a flash fired). By the time
  // flash() runs the button is already re-enabled (see the `finally` blocks
  // above), so the label span is the one Tailwind's group-disabled variant
  // is showing.
  flash(button, label, klass) {
    if (!button) return
    const labelEl = this.hasLabelTarget ? this.labelTarget : button
    const original = labelEl.textContent
    labelEl.textContent = label
    button.classList.add(klass)
    setTimeout(() => {
      labelEl.textContent = original
      button.classList.remove(klass)
    }, 1800)
  }
}
